import React, { useState, useMemo, useEffect } from 'react';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import Tree from 'packages/ui/Arborist/components/Tree';
import CardSelection from 'packages/ui/Arborist/components/CardSelection';
import TabPanel from 'components/global/TabPanel';
import { toast } from 'components/global/CUK/Toast/Toast';
import TreeSearch from 'packages/ui/Arborist/components/Search';
import TreeProvider, { useTreeContext, useSitesSelected } from 'packages/ui/Arborist/TreeProvider';
import TriggerSelection from 'packages/ui/Arborist/components/TriggerSelection';
import convertAccounts, { deserializeSelection } from 'packages/ui/Arborist/utils';
import SearchField from 'packages/ui/Arborist/components/Search/SearchField/SearchField';
import ButtonUnfold, { ButtonSites } from 'packages/ui/Arborist/components/Buttons';
import { useEditionFormContext } from '../Components/EditionFormWrapper';
import { STEPS } from '../types';
import LoadingButton from 'packages/ui/Components/LoadingButton';
import ButtonModal from 'packages/ui/Components/Button';

const TopBar = ({ disabled, onSearchChange, onViewChange = () => {}, showSelection, nbSitesSelected }) => {
  const { isFolded, onFoldedToggle } = useTreeContext();

  return (
    <Stack direction="row" gap={2} sx={{ maxHeight: '40px' }}>
      <SearchField 
        disabled={disabled} 
        onChange={onSearchChange} 
        maxLabelWidth="120px" 
      />
      <ButtonSites 
        active={showSelection}
        count={nbSitesSelected}
        onClick={() => onViewChange()}
      />
      <ButtonUnfold isUnfolded={!isFolded} onClick={onFoldedToggle} />
    </Stack>
  );
};

const AccountsPerimeter = ({ isEditMode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setActiveStep, handleSave } = useEditionFormContext();
  const { getValues, setValue, formState: { errors } } = useFormContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSelection, setShowSelection] = useState(false);

  const { accounts: treeAccounts, selection, onNodeAdd, onNodeRemove, waiting, treeRef } = useTreeContext();
  const { sites: sitesSelected, count: nbSitesSelected } = useSitesSelected({ selection });

  const dataTree = useMemo(() => {
    return convertAccounts({ accounts: treeAccounts });
  }, [treeAccounts]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handlePrevious = () => {
    setActiveStep(STEPS.CONTENT);
  };

  const handleSaveClick = async () => {
    if (Object.entries(errors).length > 0) {
      toast.error(t('auth.admin.edit.form_errors'));
      return;
    }
    if (nbSitesSelected === 0) {
      toast.error(t('auth.admin.edit.sites_selected_errors'));
      return;
    }

    setLoading(true);
    try {
      const {parent_ids, site_ids} = deserializeSelection(selection);
      const formData = getValues();
      const updatedFormData = {
        ...formData,
        sf_account_ids: [...parent_ids, ...site_ids],
        accounts: selection
      };
      await handleSave(updatedFormData);
    } catch (error) {
      console.error(error);
      toast.error(t('documents.edition.toaster.create_update_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleToast = (account) => {
    if (!account.is_site) {
      toast.info(
        t('documents.edition.toaster.account_selected', {
          account_name: account.name
        })
      );
    }
  };

  useEffect(() => {
    const {parent_ids, site_ids} = deserializeSelection(selection);
    setValue('sf_account_ids', [...parent_ids, ...site_ids]);
    setValue('accounts', selection);
  }, [selection, setValue]);

  return (
    <TabPanel>
      <Stack mt={1} spacing={2} sx={{ cursor: waiting ? 'wait' : 'inherit', height: '100%', width: '500px', minHeight: '300px' }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
            <Stack sx={{ flex: 1, gap: 2 }}>
              <TopBar
                onSearchChange={handleSearchChange}
                onViewChange={() => setShowSelection(prev => !prev)}
                showSelection={showSelection}
                nbSitesSelected={nbSitesSelected}
              />
              {searchTerm.length ? (
                <TreeSearch
                  hideTopBar={true}
                  ref={treeRef}
                  name={searchTerm}
                  view="default"
                  onNodeSelect={(node, checked) => {
                    if (checked) {
                      const account = node.data.data;
                      handleToast(account);
                    }
                  }}
                />
              ) : showSelection ? (
                <CardSelection
                  nbSites={nbSitesSelected}
                  accounts={sitesSelected}
                  onRemoveAccount={account => {
                    onNodeRemove({ id: account.sf_id });
                  }}
                  onRemoveAccountParent={account => {
                    onNodeRemove({id: account.sf_id});
                  }}
                />
              ) : (
                <>
                  <Tree
                    ref={treeRef}
                    data={dataTree}
                    openByDefault
                    onNodeSelect={async (node, checked) => {
                      if (checked) {
                        if (!waiting) {
                          await onNodeAdd(node);
                          const account = node.data.data;
                          handleToast(account);
                        }
                      } else {
                        onNodeRemove(node);
                      }
                    }}
                  />
                  <TriggerSelection treeRef={treeRef} tree={dataTree} />
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between" width="100%">
          <ButtonModal 
            variantType="cancel" 
            onClick={() => navigate({ pathname: '..' })}
            sx={{ height: '44px' }}
          >
            {t('documents.edition.buttons.cancel')}
          </ButtonModal>
          <Stack direction="row" gap={2}>
            <ButtonModal 
              variantType="prev" 
              onClick={handlePrevious}
              sx={{ height: '44px' }}
            >
              {t('documents.edition.buttons.previous')}
            </ButtonModal>
            <LoadingButton 
              height="44px" 
              onClick={handleSaveClick} 
              loading={loading}
            >
              {isEditMode ? t('documents.edition.buttons.edit') : t('documents.edition.buttons.save')}
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    </TabPanel>
  );
};

const AccountsPerimeterWrapper = ({ accounts, ...props }) => {
  return (
    <TreeProvider initialSelection={accounts || []}>
      <AccountsPerimeter {...props} accounts={accounts} />
    </TreeProvider>
  );
};

export default AccountsPerimeterWrapper;
